/**
 * SettingsModal.js
 *
 * Phase 10 — Per-user settings (mouse sens, HUD opacity, master/sfx/ambient
 * volumes, invert Y). Reads/writes both `localStorage` (instant) and the
 * `/api/settings/me` backend (cross-device sync). Backend is the source of
 * truth on next load; localStorage is the optimistic offline cache.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Switch } from './ui/switch';
import { Settings as SettingsIcon, Volume2, MousePointer2, Eye, X, RotateCcw, Gauge } from 'lucide-react';
import { toast } from 'sonner';
import { getAudioSystem } from '../game/AudioSystem';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const LS_KEY = 'arc-unc-settings-v1';

const QUALITY_LABELS = {
  low: 'Low',
  medium: 'Medium',
  cinematic: 'Cinematic',
};

const DEFAULTS = {
  mouse_sensitivity: 1.0,
  hud_opacity: 1.0,
  master_volume: 0.8,
  sfx_volume: 0.8,
  ambient_volume: 0.6,
  invert_y: false,
  graphics_quality: 'cinematic',
};

export function SettingsModal({ open, onClose }) {
  const { api } = useAuth();
  const [values, setValues] = useState(DEFAULTS);
  const [busy, setBusy] = useState(false);

  // Load: localStorage instantly, then backend (overrides)
  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setValues((v) => ({ ...v, ...parsed }));
      }
    } catch (_e) { /* noop */ }
    (async () => {
      try {
        const { data } = await api.get('/api/settings/me');
        if (data) setValues((v) => ({ ...v, ...data }));
      } catch (_e) { /* noop */ }
    })();
  }, [api, open]);

  const persist = useCallback(async (next) => {
    setValues(next);
    try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch (_e) { /* noop */ }
    // Apply audio live
    try {
      getAudioSystem().setVolumes({
        master: next.master_volume,
        sfx: next.sfx_volume,
        ambient: next.ambient_volume,
      });
    } catch (_e) { /* noop */ }
    // Apply HUD opacity live via CSS variable so widgets can opt-in
    try {
      document.documentElement.style.setProperty('--hud-opacity', String(next.hud_opacity));
    } catch (_e) { /* noop */ }
    // Sync to backend (debounced server-side write is fine)
    try {
      setBusy(true);
      await api.put('/api/settings/me', next);
    } catch (_e) {
      // Backend save failed — keep local copy and toast quietly
      toast.error('Could not sync settings to server (saved locally)');
    } finally {
      setBusy(false);
    }
  }, [api]);

  const update = useCallback((patch) => persist({ ...values, ...patch }), [persist, values]);

  const reset = useCallback(() => {
    persist({ ...DEFAULTS });
    toast.success('Settings reset to defaults');
  }, [persist]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[2200] flex items-center justify-center"
      onClick={onClose}
      data-testid="settings-modal"
    >
      <div className="absolute inset-0 bg-[#02060f]/85 backdrop-blur-md" />
      <div
        className="relative w-[min(560px,96vw)] max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-700/60 bg-[#040912]/95 shadow-[0_0_0_1px_rgba(148,163,184,0.15)] p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-modal-title"
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <SettingsIcon className="w-6 h-6 text-slate-200" />
            <h2
              id="settings-modal-title"
              className="text-xl font-bold text-slate-100"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              data-testid="settings-modal-title"
            >
              Settings
            </h2>
          </div>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="settings-modal-close">
            <X className="w-4 h-4 mr-2" /> Close
          </Button>
        </div>

        <div className="space-y-6">
          <Section
            icon={<MousePointer2 className="w-4 h-4" />}
            label="Mouse Sensitivity"
            value={values.mouse_sensitivity.toFixed(2)}
          >
            <Slider
              min={0.2} max={3.0} step={0.05}
              value={[values.mouse_sensitivity]}
              onValueChange={([v]) => update({ mouse_sensitivity: clamp(v, 0.2, 3.0) })}
              data-testid="settings-mouse-sens-slider"
            />
          </Section>

          <Section
            icon={<MousePointer2 className="w-4 h-4 rotate-180" />}
            label="Invert Y Axis"
            value={values.invert_y ? 'On' : 'Off'}
          >
            <div className="flex items-center justify-end">
              <Switch
                checked={!!values.invert_y}
                onCheckedChange={(checked) => update({ invert_y: !!checked })}
                data-testid="settings-invert-y-switch"
              />
            </div>
          </Section>

          <Section
            icon={<Eye className="w-4 h-4" />}
            label="HUD Opacity"
            value={`${Math.round(values.hud_opacity * 100)}%`}
          >
            <Slider
              min={0.4} max={1.0} step={0.05}
              value={[values.hud_opacity]}
              onValueChange={([v]) => update({ hud_opacity: clamp(v, 0.4, 1.0) })}
              data-testid="settings-hud-opacity-slider"
            />
          </Section>

          <Section
            icon={<Gauge className="w-4 h-4" />}
            label="Graphics Quality"
            value={QUALITY_LABELS[values.graphics_quality] || 'Cinematic'}
          >
            <Select
              value={values.graphics_quality}
              onValueChange={(v) => update({ graphics_quality: v })}
            >
              <SelectTrigger className="w-full" data-testid="settings-graphics-quality-trigger">
                <SelectValue placeholder="Cinematic" />
              </SelectTrigger>
              <SelectContent className="z-[2300]">
                <SelectItem value="low" data-testid="settings-graphics-low">Low — best FPS</SelectItem>
                <SelectItem value="medium" data-testid="settings-graphics-medium">Medium — balanced</SelectItem>
                <SelectItem value="cinematic" data-testid="settings-graphics-cinematic">Cinematic — best visuals</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1.5">
              Post-FX update instantly; shadows, foliage &amp; water refresh after you reload the engine.
            </p>
          </Section>

          <hr className="border-slate-800/60" />

          <Section
            icon={<Volume2 className="w-4 h-4" />}
            label="Master Volume"
            value={`${Math.round(values.master_volume * 100)}%`}
          >
            <Slider
              min={0} max={1} step={0.02}
              value={[values.master_volume]}
              onValueChange={([v]) => update({ master_volume: clamp(v, 0, 1) })}
              data-testid="settings-master-volume-slider"
            />
          </Section>

          <Section
            icon={<Volume2 className="w-4 h-4" />}
            label="SFX Volume"
            value={`${Math.round(values.sfx_volume * 100)}%`}
          >
            <Slider
              min={0} max={1} step={0.02}
              value={[values.sfx_volume]}
              onValueChange={([v]) => update({ sfx_volume: clamp(v, 0, 1) })}
              data-testid="settings-sfx-volume-slider"
            />
          </Section>

          <Section
            icon={<Volume2 className="w-4 h-4" />}
            label="Ambient Volume"
            value={`${Math.round(values.ambient_volume * 100)}%`}
          >
            <Slider
              min={0} max={1} step={0.02}
              value={[values.ambient_volume]}
              onValueChange={([v]) => update({ ambient_volume: clamp(v, 0, 1) })}
              data-testid="settings-ambient-volume-slider"
            />
          </Section>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <span className="text-xs text-slate-500">
            {busy ? 'Saving…' : 'Saved locally + synced to your account'}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={reset}
            data-testid="settings-reset-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" /> Reset to defaults
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ icon, label, value, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm text-slate-200">
          <span className="text-slate-400">{icon}</span>
          {label}
        </div>
        <span className="text-xs text-slate-400 tabular-nums">{value}</span>
      </div>
      {children}
    </div>
  );
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, Number(v)));
}

/** Helper hook to read live settings (useful if Explore3D wants live mouse sens). */
export function useUserSettings() {
  const { api } = useAuth();
  const [values, setValues] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
    } catch (_e) { /* noop */ }
    return DEFAULTS;
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/settings/me');
        if (!cancelled && data) {
          setValues((v) => ({ ...v, ...data }));
          // Mirror to localStorage for offline mode
          try { localStorage.setItem(LS_KEY, JSON.stringify({ ...DEFAULTS, ...data })); } catch (_e) { /* noop */ }
        }
      } catch (_e) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [api]);

  return values;
}

export default SettingsModal;
